import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const handleError = (error) => {
  console.error('Team API Error:', error);
  if (error.response?.status === 503) {
    return { success: false, error: 'Service temporarily unavailable' };
  }
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

export const createTeam = async (teamData, token) => {
  try {
    const payload = {
      name: teamData.name,
      department_id: teamData.departmentId
    };

    if (teamData.parentId) payload.parent_id = teamData.parentId;

    const response = await axios.post(
      `${BASE_URL}/user/v1/team`,
      payload,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No team data received');

    return {
      success: response.data.status === 0,
      team: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const activateTeam = async (teamId, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/team/activate`,
      { team_id: teamId },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getTeamById = async (teamId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/team/${teamId}`,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No team data received');

    return {
      success: response.data.status === 0,
      team: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllTeams = async (params, token) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.pagination) {
      queryParams.append('pagination.page', params.pagination.page || 1);
      queryParams.append('pagination.page_size', params.pagination.pageSize || 10);
    }

    if (params.sorting) {
      queryParams.append('sorting.sort_by', params.sorting.sort_by);
      queryParams.append('sorting.order', params.sorting.order);
    }

    if (params.nameFilter) queryParams.append('name_filter', params.nameFilter);
    if (params.departmentId) queryParams.append('department_id', params.departmentId);
    if (params.leadId) queryParams.append('lead_id', params.leadId);
    if (params.parentId) queryParams.append('parent_id', params.parentId);

    const response = await axios.get(
      `${BASE_URL}/user/v1/teams?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    const data = response.data;
    if (!data.data) throw new Error('No teams data received');

    return {
      success: data.status === 0,
      teams: data.data,
      pagination: {
        totalCount: data.totalCount,
        pageCount: data.pageCount,
        page: data.page,
        pageSize: data.pageSize
      }
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateTeam = async (teamData, token) => {
  try {
    const payload = {
      id: teamData.id,
      name: teamData.name
    };

    if (teamData.parentId) payload.parent_id = teamData.parentId;
    if (teamData.departmentId) payload.department_id = teamData.departmentId;

    const response = await axios.patch(
      `${BASE_URL}/user/v1/team/${teamData.id}`,
      payload,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No team data received');

    return {
      success: response.data.status === 0,
      team: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteTeam = async (teamId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/team/${teamId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromTeam = async (teamId, userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/team/${teamId}/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllTeamLeadsCredentials = async (teamId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/team/${teamId}/leads`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      leads: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};
